/*
  assets/store-locator.js

  Worldwide authorized retailer locator for the wholesale section.

  The map is a single, persistent, standard 2D Google Map (no globe, no
  3D projection, at every zoom level including fully zoomed out). Nearby
  stores are grouped into numbered clusters using Google's own
  @googlemaps/markerclusterer library, which automatically regroups them
  as the map is panned or zoomed, breaking clusters apart into smaller
  clusters and then individual pins as the user zooms in.

  Because the map is now the only map (there is no globe fallback), a
  Google Maps API key is required for the map itself to render. Search,
  the country dropdown, "Use my location", and the results list all work
  fully even without a key, only the visual map goes blank with a short
  explanation in its place.

  Loads, in this order:
    1. assets/retailers.json       (the bulk retailer data, fetched here)
    2. the Google Maps JavaScript API (loaded by this file, via the key
       passed in from the section's Liquid markup)
    3. https://unpkg.com/@googlemaps/markerclusterer (loaded by a <script>
       tag in the section's Liquid markup, before this file)

  Retailers added as "Retailer" blocks in the theme editor (see the
  section's schema) are read from a #pp-locator-block-data JSON script
  tag the Liquid markup writes out, and merged into the same list as
  retailers.json, so admin can add new stores without touching code.

  Each retailer can carry up to three tags, shown under its phone line:
  "Pet Care Service", "Online Shop", and "In-Store" all come straight
  from retailer data (the pet_care_service / online_store / in_store
  fields), set per retailer, independent of each other. A retailer can
  have a website link (shown as "Visit Website") without actually
  selling online, so "Online Shop" is its own explicit field rather
  than being inferred from whether a website is present.

  On load, the results default to the visitor's own country via a
  silent IP-based lookup, no permission prompt. If Proactive Paws has
  no retailer there yet, it shows every retailer in the visitor's own
  part of the world together (same UN sub-region, e.g. "South-eastern
  Asia", combined across however many countries that covers) before
  falling back further to the single nearest retailer anywhere on the
  globe, if even that part of the world has none. This is separate
  from the "Use My Location" button, which is an explicit, user-
  triggered GPS lookup for exact nearest-store distance. See the
  "Automatic country detection" section below.
*/

(function () {
  'use strict';

  var rootEl = document.getElementById('pp-locator-root');
  if (!rootEl) return;

  var retailersUrl = rootEl.getAttribute('data-retailers-url');
  var googleMapsKey = rootEl.getAttribute('data-google-maps-key') || '';
  var hasGoogleMaps = googleMapsKey.length > 0;

  var mapEl = document.getElementById('pp-locator-map');
  var mapWrapEl = document.querySelector('.pp-locator__map-wrap');
  var resultsHeadingEl = document.getElementById('pp-locator-results-heading');
  var resultsCountEl = document.getElementById('pp-locator-results-count');
  var resultsPanelEl = document.getElementById('pp-locator-results');
  var searchInputEl = document.getElementById('pp-locator-search');
  var countryTriggerEl = document.getElementById('pp-locator-country-trigger');
  var countryCurrentLabelEl = document.getElementById('pp-locator-country-current');
  var countryMenuEl = document.getElementById('pp-locator-country-menu');
  var clearBtnEl = document.getElementById('pp-locator-clear');
  var geoBtnEl = document.getElementById('pp-locator-geo');
  var sampleBannerEl = document.getElementById('pp-locator-sample-banner');
  var statusEl = document.getElementById('pp-locator-status');

  var prefersReducedMotion = false;
  try {
    prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  var allRetailers = [];
  var byCountryNorm = {};
  var selectedCountryValue = '';

  var map = null;
  var infoWindow = null;
  var clusterer = null;
  var markersByKey = null;
  var mapReady = false;
  var pendingFilter = null;
  var currentMapFilter = { list: [], focusPoint: null };
  // Which retailer's card should carry the "is-active" highlight: the
  // one that was clicked/selected, or whose address is currently
  // showing on the map (its info window is open). Kept as its pinKey()
  // so it survives a results-list re-render (search, country change,
  // etc.) rather than pointing at a DOM node that no longer exists.
  var activePinKey = null;

  var PIN_SIZE = 36;
  var PIN_SIZE_MATCH = 50;
  var PIN_DIMMED_OPACITY = 0.8; // kept high on purpose: a non-matching pin should still read clearly on the map, not disappear
  var PIN_ASPECT = 42 / 55; // native viewBox is 42 wide by 55 tall
  var CLUSTER_COLOR = '#A36043';

  var LOCATION_PIN_ICON =
    '<svg class="pp-locator__country-option-icon" width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">'+
    '<path d="M6.875 3.75C6.25693 3.75 5.65275 3.93328 5.13884 4.27666C4.62494 4.62004 4.2244 5.1081 3.98788 5.67911C3.75135 6.25013 3.68947 6.87847 3.81005 7.48466C3.93062 8.09085 4.22825 8.64767 4.66529 9.08471C5.10233 9.52175 5.65915 9.81938 6.26534 9.93995C6.87153 10.0605 7.49987 9.99865 8.07089 9.76212C8.6419 9.5256 9.12996 9.12506 9.47334 8.61116C9.81672 8.09725 10 7.49307 10 6.875C10 6.0462 9.67076 5.25134 9.08471 4.66529C8.49866 4.07924 7.7038 3.75 6.875 3.75ZM6.875 8.75C6.50416 8.75 6.14165 8.64003 5.83331 8.43401C5.52496 8.22798 5.28464 7.93514 5.14273 7.59253C5.00081 7.24992 4.96368 6.87292 5.03603 6.50921C5.10837 6.14549 5.28695 5.8114 5.54917 5.54917C5.8114 5.28695 6.14549 5.10837 6.50921 5.03603C6.87292 4.96368 7.24992 5.00081 7.59253 5.14273C7.93514 5.28464 8.22798 5.52496 8.43401 5.83331C8.64003 6.14165 8.75 6.50416 8.75 6.875C8.75 7.37228 8.55246 7.84919 8.20083 8.20083C7.84919 8.55246 7.37228 8.75 6.875 8.75ZM6.875 0C5.05227 0.00206776 3.30479 0.72706 2.01592 2.01592C0.72706 3.30479 0.00206776 5.05227 0 6.875C0 9.32812 1.13359 11.9281 3.28125 14.3945C4.24627 15.509 5.33239 16.5126 6.51953 17.3867C6.62462 17.4603 6.74982 17.4998 6.87812 17.4998C7.00643 17.4998 7.13163 17.4603 7.23672 17.3867C8.42168 16.5123 9.50569 15.5087 10.4688 14.3945C12.6133 11.9281 13.75 9.32812 13.75 6.875C13.7479 5.05227 13.0229 3.30479 11.7341 2.01592C10.4452 0.72706 8.69773 0.00206776 6.875 0ZM6.875 16.0938C5.58359 15.0781 1.25 11.3477 1.25 6.875C1.25 5.38316 1.84263 3.95242 2.89752 2.89752C3.95242 1.84263 5.38316 1.25 6.875 1.25C8.36684 1.25 9.79758 1.84263 10.8525 2.89752C11.9074 3.95242 12.5 5.38316 12.5 6.875C12.5 11.3461 8.16641 15.0781 6.875 16.0938Z" fill="#3D3533"/>'+
    '</svg>';

  var PIN_ICON_SVG =
    '<svg width="42" height="55" viewBox="0 0 42 55" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M21 0C32.598 0 42 9.40202 42 21C42 22.4157 41.8599 23.7986 41.5929 25.1358C39.7394 39.1032 21.1104 55 21.1104 55C21.1104 55 5.25689 41.4717 1.34456 28.4096C0.475507 26.1054 0 23.6083 0 21C0 9.40202 9.40202 0 21 0Z" fill="#A36043"/>' +
      '<path d="M38 21C38 11.6112 30.3888 4 21 4C11.6112 4 4 11.6112 4 21C4 30.3888 11.6112 38 21 38C30.3888 38 38 30.3888 38 21Z" fill="#FCF4F0"/>' +
      '<path d="M27.5625 17.25C27.1299 17.25 26.7069 17.3783 26.3472 17.6187C25.9875 17.859 25.7071 18.2007 25.5415 18.6004C25.3759 19.0001 25.3326 19.4399 25.417 19.8643C25.5014 20.2886 25.7098 20.6784 26.0157 20.9843C26.3216 21.2902 26.7114 21.4986 27.1357 21.583C27.5601 21.6674 27.9999 21.6241 28.3996 21.4585C28.7993 21.2929 29.141 21.0125 29.3813 20.6528C29.6217 20.2931 29.75 19.8701 29.75 19.4375C29.75 18.8573 29.5195 18.3009 29.1093 17.8907C28.6991 17.4805 28.1427 17.25 27.5625 17.25ZM27.5625 20.375C27.3771 20.375 27.1958 20.32 27.0417 20.217C26.8875 20.114 26.7673 19.9676 26.6964 19.7963C26.6254 19.625 26.6068 19.4365 26.643 19.2546C26.6792 19.0727 26.7685 18.9057 26.8996 18.7746C27.0307 18.6435 27.1977 18.5542 27.3796 18.518C27.5615 18.4818 27.75 18.5004 27.9213 18.5714C28.0926 18.6423 28.239 18.7625 28.342 18.9167C28.445 19.0708 28.5 19.2521 28.5 19.4375C28.5 19.6861 28.4012 19.9246 28.2254 20.1004C28.0496 20.2762 27.8111 20.375 27.5625 20.375ZM16.625 19.4375C16.625 19.0049 16.4967 18.5819 16.2563 18.2222C16.016 17.8625 15.6743 17.5821 15.2746 17.4165C14.8749 17.2509 14.4351 17.2076 14.0107 17.292C13.5864 17.3764 13.1966 17.5848 12.8907 17.8907C12.5848 18.1966 12.3764 18.5864 12.292 19.0107C12.2076 19.4351 12.2509 19.8749 12.4165 20.2746C12.5821 20.6743 12.8625 21.016 13.2222 21.2563C13.5819 21.4967 14.0049 21.625 14.4375 21.625C15.0177 21.625 15.5741 21.3945 15.9843 20.9843C16.3945 20.5741 16.625 20.0177 16.625 19.4375ZM14.4375 20.375C14.2521 20.375 14.0708 20.32 13.9167 20.217C13.7625 20.114 13.6423 19.9676 13.5714 19.7963C13.5004 19.625 13.4818 19.4365 13.518 19.2546C13.5542 19.0727 13.6435 18.9057 13.7746 18.7746C13.9057 18.6435 14.0727 18.5542 14.2546 18.518C14.4365 18.4818 14.625 18.5004 14.7963 18.5714C14.9676 18.6423 15.114 18.7625 15.217 18.9167C15.32 19.0708 15.375 19.2521 15.375 19.4375C15.375 19.6861 15.2762 19.9246 15.1004 20.1004C14.9246 20.2762 14.6861 20.375 14.4375 20.375ZM18.1875 17.875C18.6201 17.875 19.0431 17.7467 19.4028 17.5063C19.7625 17.266 20.0429 16.9243 20.2085 16.5246C20.3741 16.1249 20.4174 15.6851 20.333 15.2607C20.2486 14.8364 20.0402 14.4466 19.7343 14.1407C19.4284 13.8348 19.0386 13.6264 18.6143 13.542C18.1899 13.4576 17.7501 13.5009 17.3504 13.6665C16.9507 13.8321 16.609 14.1125 16.3687 14.4722C16.1283 14.8319 16 15.2549 16 15.6875C16 16.2677 16.2305 16.8241 16.6407 17.2343C17.0509 17.6445 17.6073 17.875 18.1875 17.875ZM18.1875 14.75C18.3729 14.75 18.5542 14.805 18.7083 14.908C18.8625 15.011 18.9827 15.1574 19.0536 15.3287C19.1246 15.5 19.1432 15.6885 19.107 15.8704C19.0708 16.0523 18.9815 16.2193 18.8504 16.3504C18.7193 16.4815 18.5523 16.5708 18.3704 16.607C18.1885 16.6432 18 16.6246 17.8287 16.5536C17.6574 16.4827 17.511 16.3625 17.408 16.2083C17.305 16.0542 17.25 15.8729 17.25 15.6875C17.25 15.4389 17.3488 15.2004 17.5246 15.0246C17.7004 14.8488 17.9389 14.75 18.1875 14.75ZM23.8125 17.875C24.2451 17.875 24.6681 17.7467 25.0278 17.5063C25.3875 17.266 25.6679 16.9243 25.8335 16.5246C25.9991 16.1249 26.0424 15.6851 25.958 15.2607C25.8736 14.8364 25.6652 14.4466 25.3593 14.1407C25.0534 13.8348 24.6636 13.6264 24.2393 13.542C23.8149 13.4576 23.3751 13.5009 22.9754 13.6665C22.5757 13.8321 22.234 14.1125 21.9937 14.4722C21.7533 14.8319 21.625 15.2549 21.625 15.6875C21.625 16.2677 21.8555 16.8241 22.2657 17.2343C22.6759 17.6445 23.2323 17.875 23.8125 17.875ZM23.8125 14.75C23.9979 14.75 24.1792 14.805 24.3333 14.908C24.4875 15.011 24.6077 15.1574 24.6786 15.3287C24.7496 15.5 24.7682 15.6885 24.732 15.8704C24.6958 16.0523 24.6065 16.2193 24.4754 16.3504C24.3443 16.4815 24.1773 16.5708 23.9954 16.607C23.8135 16.6432 23.625 16.6246 23.4537 16.5536C23.2824 16.4827 23.136 16.3625 23.033 16.2083C22.93 16.0542 22.875 15.8729 22.875 15.6875C22.875 15.4389 22.9738 15.2004 23.1496 15.0246C23.3254 14.8488 23.5639 14.75 23.8125 14.75ZM25.6188 22.6297C25.3013 22.4546 25.0215 22.2185 24.7953 21.9351C24.5692 21.6517 24.4011 21.3265 24.3008 20.9781C24.093 20.2635 23.6588 19.6356 23.0636 19.1888C22.4684 18.742 21.7443 18.5004 21 18.5004C20.2558 18.5004 19.5316 18.742 18.9364 19.1888C18.3412 19.6356 17.907 20.2635 17.6992 20.9781C17.4985 21.6792 17.0279 22.272 16.3906 22.6266C15.7793 22.9573 15.2957 23.4825 15.0164 24.1189C14.737 24.7554 14.6778 25.4668 14.8481 26.1407C15.0184 26.8146 15.4085 27.4124 15.9567 27.8397C16.5049 28.267 17.1799 28.4994 17.875 28.5C18.2915 28.5012 18.704 28.418 19.0875 28.2555C20.3102 27.7512 21.6828 27.7512 22.9055 28.2555C23.6461 28.5776 24.4826 28.6004 25.2397 28.3193C25.9968 28.0382 26.6156 27.4749 26.9665 26.7475C27.3173 26.0201 27.373 25.1851 27.1217 24.4175C26.8705 23.65 26.3318 23.0096 25.6188 22.6305V22.6297ZM24.125 27.25C23.8712 27.2502 23.6201 27.1989 23.3867 27.0992C21.856 26.4669 20.137 26.4669 18.6063 27.0992C18.1614 27.2908 17.6597 27.3027 17.2062 27.1326C16.7528 26.9625 16.3827 26.6235 16.1736 26.1867C15.9644 25.7499 15.9324 25.249 16.0843 24.7891C16.2362 24.3292 16.5601 23.9459 16.9883 23.7195C17.4486 23.4653 17.8543 23.1228 18.1822 22.7117C18.5101 22.3005 18.7538 21.8288 18.8992 21.3234C19.0313 20.8684 19.3075 20.4686 19.6864 20.1841C20.0652 19.8996 20.5262 19.7458 21 19.7458C21.4738 19.7458 21.9348 19.8996 22.3136 20.1841C22.6925 20.4686 22.9687 20.8684 23.1008 21.3234C23.2467 21.8297 23.4911 22.3023 23.8199 22.714C24.1488 23.1257 24.5556 23.4685 25.0172 23.7227C25.3858 23.9204 25.6777 24.2356 25.8465 24.6184C26.0153 25.0011 26.0513 25.4293 25.9487 25.8348C25.8462 26.2403 25.6109 26.5999 25.2805 26.8563C24.95 27.1128 24.5433 27.2513 24.125 27.25Z" fill="#A36043"/>' +
    '</svg>';
  var PIN_ICON_URL = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(PIN_ICON_SVG);

  function pinIcon(height) {
    var width = height * PIN_ASPECT;
    return {
      url: PIN_ICON_URL,
      scaledSize: new google.maps.Size(width, height),
      anchor: new google.maps.Point(width / 2, height)
    };
  }

  function norm(str) {
    return (str || '').toString().trim().toLowerCase();
  }

  function pinKey(r) {
    return r.name + '|' + r.lat + '|' + r.lng;
  }

  function buildPinMatchSet(list) {
    var set = {};
    (list || []).forEach(function (r) {
      if (typeof r.lat === 'number' && typeof r.lng === 'number') {
        set[pinKey(r)] = true;
      }
    });
    return set;
  }

  function haversineMiles(lat1, lon1, lat2, lon2) {
    var toRad = function (deg) { return (deg * Math.PI) / 180; };
    var R = 3958.8;
    var dLat = toRad(lat2 - lat1);
    var dLon = toRad(lon2 - lon1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function groupByCountry(retailers) {
    var map = {};
    retailers.forEach(function (r) {
      var key = norm(r.country);
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return map;
  }

  // ---- Automatic country detection on page load ----
  //
  // Separate from the "Use My Location" button (setUpGeolocation,
  // below), which is an explicit, user-triggered GPS lookup: a
  // permission prompt, then an exact-distance sort of every retailer.
  // This one runs silently as soon as the page loads, no permission
  // prompt, using the visitor's IP address to guess their country and
  // default the "By Country" filter to it, so a first-time visitor
  // doesn't have to browse the full world list to find their own
  // country's retailers. If Proactive Paws has no retailer in that
  // country yet, it falls back to whichever country has the nearest
  // one. It only ever sets the INITIAL view: once the visitor searches,
  // picks a country, clears the filter, or uses "Use My Location"
  // themselves, their own choice stands, never overwritten by this.
  var GEO_IP_ENDPOINT = 'https://ipwho.is/';
  var GEO_IP_TIMEOUT_MS = 2500;
  var userHasInteracted = false;

  function markUserInteracted() {
    userHasInteracted = true;
  }

  // A handful of countries are commonly written a few different ways:
  // the short/common name an IP-geolocation service returns vs. the
  // longer formal name retailers.json (or a theme editor admin) might
  // use. Normalizing both sides the same way means detection still
  // works without retailers.json needing to spell a country any
  // particular way.
  function normalizeCountryName(str) {
    var s = norm(str);
    if (s === 'united states of america' || s === 'usa' || s === 'u.s.a.' || s === 'u.s.' || s === 'us') {
      return 'united states';
    }
    if (s === 'united kingdom of great britain and northern ireland' || s === 'great britain' || s === 'uk') {
      return 'united kingdom';
    }
    if (s === 'uae') {
      return 'united arab emirates';
    }
    return s;
  }

  // Maps a country name to its UN geoscheme sub-region (the UN's own
  // regional breakdown, see https://unstats.un.org/unsd/methodology/m49/),
  // used only by the fallback below: when the visitor's own country has
  // no retailer, prefer a retailer in the same part of the world over
  // whichever retailer happens to be closest as the crow flies. Hong
  // Kong is closer to the Philippines in raw distance than Singapore,
  // Indonesia, or Malaysia are, but those three are "South-eastern
  // Asia" while Hong Kong is "Eastern Asia", so a Philippines-based
  // visitor gets matched within Southeast Asia first, not simply
  // routed to whichever single country is nearest on the globe.
  var COUNTRY_SUBREGION = {
    'afghanistan': 'Southern Asia',
    'albania': 'Southern Europe',
    'algeria': 'Northern Africa',
    'american samoa': 'Polynesia',
    'andorra': 'Southern Europe',
    'angola': 'Middle Africa',
    'anguilla': 'Caribbean',
    'antarctica': 'Antarctica',
    'antigua and barbuda': 'Caribbean',
    'argentina': 'South America',
    'armenia': 'Western Asia',
    'aruba': 'Caribbean',
    'australia': 'Australia and New Zealand',
    'austria': 'Western Europe',
    'azerbaijan': 'Western Asia',
    'bahamas': 'Caribbean',
    'bahrain': 'Western Asia',
    'bangladesh': 'Southern Asia',
    'barbados': 'Caribbean',
    'belarus': 'Eastern Europe',
    'belgium': 'Western Europe',
    'belize': 'Central America',
    'benin': 'Western Africa',
    'bermuda': 'Northern America',
    'bhutan': 'Southern Asia',
    'bolivia': 'South America',
    'bonaire, saint eustatius and saba': 'Caribbean',
    'bosnia and herzegovina': 'Southern Europe',
    'botswana': 'Southern Africa',
    'bouvet island': 'South America',
    'brazil': 'South America',
    'british indian ocean territory': 'Eastern Africa',
    'british virgin islands': 'Caribbean',
    'brunei': 'South-eastern Asia',
    'brunei darussalam': 'South-eastern Asia',
    'bulgaria': 'Eastern Europe',
    'burkina faso': 'Western Africa',
    'burma': 'South-eastern Asia',
    'burundi': 'Eastern Africa',
    'cabo verde': 'Western Africa',
    'cambodia': 'South-eastern Asia',
    'cameroon': 'Middle Africa',
    'canada': 'Northern America',
    'cape verde': 'Western Africa',
    'cayman islands': 'Caribbean',
    'central african republic': 'Middle Africa',
    'chad': 'Middle Africa',
    'chile': 'South America',
    'china': 'Eastern Asia',
    'christmas island': 'Australia and New Zealand',
    'cocos (keeling) islands': 'Australia and New Zealand',
    'colombia': 'South America',
    'comoros': 'Eastern Africa',
    'congo republic': 'Middle Africa',
    'cook islands': 'Polynesia',
    'costa rica': 'Central America',
    'cote d\'ivoire': 'Western Africa',
    'croatia': 'Southern Europe',
    'cuba': 'Caribbean',
    'curaçao': 'Caribbean',
    'cyprus': 'Western Asia',
    'czechia': 'Eastern Europe',
    'côte d\'ivoire': 'Western Africa',
    'denmark': 'Northern Europe',
    'djibouti': 'Eastern Africa',
    'dominica': 'Caribbean',
    'dominican republic': 'Caribbean',
    'dr congo': 'Middle Africa',
    'ecuador': 'South America',
    'egypt': 'Northern Africa',
    'el salvador': 'Central America',
    'equatorial guinea': 'Middle Africa',
    'eritrea': 'Eastern Africa',
    'estonia': 'Northern Europe',
    'eswatini': 'Southern Africa',
    'ethiopia': 'Eastern Africa',
    'falkland islands': 'South America',
    'faroe islands': 'Northern Europe',
    'fiji': 'Melanesia',
    'finland': 'Northern Europe',
    'france': 'Western Europe',
    'french guiana': 'South America',
    'french polynesia': 'Polynesia',
    'french southern territories': 'Eastern Africa',
    'gabon': 'Middle Africa',
    'gambia': 'Western Africa',
    'georgia': 'Western Asia',
    'germany': 'Western Europe',
    'ghana': 'Western Africa',
    'gibraltar': 'Southern Europe',
    'greece': 'Southern Europe',
    'greenland': 'Northern America',
    'grenada': 'Caribbean',
    'guadeloupe': 'Caribbean',
    'guam': 'Micronesia',
    'guatemala': 'Central America',
    'guernsey': 'Northern Europe',
    'guinea': 'Western Africa',
    'guinea-bissau': 'Western Africa',
    'guyana': 'South America',
    'haiti': 'Caribbean',
    'heard and mcdonald islands': 'Australia and New Zealand',
    'holy see': 'Southern Europe',
    'honduras': 'Central America',
    'hong kong': 'Eastern Asia',
    'hungary': 'Eastern Europe',
    'iceland': 'Northern Europe',
    'india': 'Southern Asia',
    'indonesia': 'South-eastern Asia',
    'iran': 'Southern Asia',
    'iraq': 'Western Asia',
    'ireland': 'Northern Europe',
    'isle of man': 'Northern Europe',
    'israel': 'Western Asia',
    'italy': 'Southern Europe',
    'ivory coast': 'Western Africa',
    'jamaica': 'Caribbean',
    'japan': 'Eastern Asia',
    'jersey': 'Northern Europe',
    'jordan': 'Western Asia',
    'kazakhstan': 'Central Asia',
    'kenya': 'Eastern Africa',
    'kiribati': 'Micronesia',
    'kosovo': 'Southern Europe',
    'kuwait': 'Western Asia',
    'kyrgyzstan': 'Central Asia',
    'laos': 'South-eastern Asia',
    'latvia': 'Northern Europe',
    'lebanon': 'Western Asia',
    'lesotho': 'Southern Africa',
    'liberia': 'Western Africa',
    'libya': 'Northern Africa',
    'liechtenstein': 'Western Europe',
    'lithuania': 'Northern Europe',
    'luxembourg': 'Western Europe',
    'macao': 'Eastern Asia',
    'macau': 'Eastern Asia',
    'madagascar': 'Eastern Africa',
    'malawi': 'Eastern Africa',
    'malaysia': 'South-eastern Asia',
    'maldives': 'Southern Asia',
    'mali': 'Western Africa',
    'malta': 'Southern Europe',
    'marshall islands': 'Micronesia',
    'martinique': 'Caribbean',
    'mauritania': 'Western Africa',
    'mauritius': 'Eastern Africa',
    'mayotte': 'Eastern Africa',
    'mexico': 'Central America',
    'micronesia, fed. sts.': 'Micronesia',
    'moldova': 'Eastern Europe',
    'monaco': 'Western Europe',
    'mongolia': 'Eastern Asia',
    'montenegro': 'Southern Europe',
    'montserrat': 'Caribbean',
    'morocco': 'Northern Africa',
    'mozambique': 'Eastern Africa',
    'myanmar': 'South-eastern Asia',
    'namibia': 'Southern Africa',
    'nauru': 'Polynesia',
    'nepal': 'Southern Asia',
    'netherlands': 'Western Europe',
    'new caledonia': 'Melanesia',
    'new zealand': 'Australia and New Zealand',
    'nicaragua': 'Central America',
    'niger': 'Western Africa',
    'nigeria': 'Western Africa',
    'niue': 'Polynesia',
    'norfolk island': 'Australia and New Zealand',
    'north korea': 'Eastern Asia',
    'north macedonia': 'Southern Europe',
    'northern mariana islands': 'Micronesia',
    'norway': 'Northern Europe',
    'oman': 'Western Asia',
    'pakistan': 'Southern Asia',
    'palau': 'Micronesia',
    'palestine': 'Western Asia',
    'panama': 'Central America',
    'papua new guinea': 'Melanesia',
    'paraguay': 'South America',
    'peru': 'South America',
    'philippines': 'South-eastern Asia',
    'pitcairn': 'Polynesia',
    'poland': 'Eastern Europe',
    'portugal': 'Southern Europe',
    'puerto rico': 'Caribbean',
    'qatar': 'Western Asia',
    'romania': 'Eastern Europe',
    'russia': 'Eastern Europe',
    'rwanda': 'Eastern Africa',
    'réunion': 'Eastern Africa',
    'saint-martin': 'Caribbean',
    'samoa': 'Polynesia',
    'san marino': 'Southern Europe',
    'sao tome and principe': 'Middle Africa',
    'saudi arabia': 'Western Asia',
    'senegal': 'Western Africa',
    'serbia': 'Southern Europe',
    'seychelles': 'Eastern Africa',
    'sierra leone': 'Western Africa',
    'singapore': 'South-eastern Asia',
    'sint maarten': 'Caribbean',
    'slovakia': 'Eastern Europe',
    'slovenia': 'Southern Europe',
    'solomon islands': 'Melanesia',
    'somalia': 'Eastern Africa',
    'south africa': 'Southern Africa',
    'south georgia and south sandwich is.': 'South America',
    'south korea': 'Eastern Asia',
    'south sudan': 'Eastern Africa',
    'spain': 'Southern Europe',
    'sri lanka': 'Southern Asia',
    'st. barths': 'Caribbean',
    'st. helena': 'Western Africa',
    'st. kitts and nevis': 'Caribbean',
    'st. lucia': 'Caribbean',
    'st. pierre and miquelon': 'Northern America',
    'st. vincent and the grenadines': 'Caribbean',
    'sudan': 'Northern Africa',
    'suriname': 'South America',
    'svalbard and jan mayen islands': 'Northern Europe',
    'swaziland': 'Southern Africa',
    'sweden': 'Northern Europe',
    'switzerland': 'Western Europe',
    'syria': 'Western Asia',
    'taiwan': 'Eastern Asia',
    'tajikistan': 'Central Asia',
    'tanzania': 'Eastern Africa',
    'thailand': 'South-eastern Asia',
    'timor-leste': 'South-eastern Asia',
    'togo': 'Western Africa',
    'tokelau': 'Polynesia',
    'tonga': 'Polynesia',
    'trinidad and tobago': 'Caribbean',
    'tunisia': 'Northern Africa',
    'turkmenistan': 'Central Asia',
    'turks and caicos islands': 'Caribbean',
    'tuvalu': 'Polynesia',
    'türkiye': 'Western Asia',
    'uganda': 'Eastern Africa',
    'ukraine': 'Eastern Europe',
    'united arab emirates': 'Western Asia',
    'united kingdom': 'Northern Europe',
    'united states': 'Northern America',
    'united states minor outlying islands': 'Micronesia',
    'united states virgin islands': 'Caribbean',
    'uruguay': 'South America',
    'uzbekistan': 'Central Asia',
    'vanuatu': 'Melanesia',
    'vatican': 'Southern Europe',
    'vatican city': 'Southern Europe',
    'venezuela': 'South America',
    'vietnam': 'South-eastern Asia',
    'wallis and futuna islands': 'Polynesia',
    'western sahara': 'Northern Africa',
    'yemen': 'Western Asia',
    'zambia': 'Eastern Africa',
    'zimbabwe': 'Eastern Africa',
    'åland islands': 'Northern Europe'
  };

  // Looks up a country's UN sub-region, after the same name
  // normalization used for matching retailer countries, so
  // "United States of America" (how retailers.json might spell it) and
  // "United States" (how an IP-geolocation lookup might spell it) land
  // on the same key.
  function countrySubregion(countryName) {
    return COUNTRY_SUBREGION[normalizeCountryName(countryName)] || null;
  }

  // Finds the exact-cased country string already used in retailer data
  // (whatever retailers.json or a theme editor admin happened to type)
  // that corresponds to the country name an IP-geolocation lookup
  // returned, so the result can be handed straight to
  // selectCountryOption() without a spelling mismatch.
  function matchRetailerCountry(apiCountryName) {
    if (!apiCountryName) return null;
    var target = normalizeCountryName(apiCountryName);
    var match = null;
    Object.keys(byCountryNorm).some(function (key) {
      var list = byCountryNorm[key];
      if (list && list.length && normalizeCountryName(list[0].country) === target) {
        match = list[0].country;
        return true;
      }
      return false;
    });
    return match;
  }

  // Resolves to { country, lat, lng } from the visitor's IP, or null if
  // the lookup fails, times out, or the service has nothing useful.
  // Never blocks the page waiting on a third-party service: whichever
  // settles first between the fetch and the timeout wins, so a slow or
  // unreachable geolocation service just falls back to the full,
  // unfiltered list instead of leaving visitors staring at nothing.
  function fetchVisitorGeo() {
    return new Promise(function (resolve) {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        resolve(null);
      }, GEO_IP_TIMEOUT_MS);

      fetch(GEO_IP_ENDPOINT)
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          if (!data || data.success === false) {
            resolve(null);
            return;
          }
          var lat = parseFloat(data.latitude);
          var lng = parseFloat(data.longitude);
          resolve({
            country: data.country || '',
            lat: isNaN(lat) ? null : lat,
            lng: isNaN(lng) ? null : lng
          });
        })
        .catch(function () {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(null);
        });
    });
  }

  // Kicked off immediately, in parallel with the retailers.json fetch
  // below, so the two round trips overlap instead of adding up.
  var geoIpPromise = fetchVisitorGeo();

  // A few UN sub-region names read fine as-is in the pill/heading
  // ("Northern Europe", "Eastern Africa"), but "South-eastern Asia" is
  // the one exception, visitors know this part of the world as
  // "Southeast Asia". Only affects display text, never the lookup key.
  function subregionDisplayName(subregion) {
    if (subregion === 'South-eastern Asia') return 'Southeast Asia';
    return subregion;
  }

  // Shows every retailer in the visitor's own sub-region together
  // (not narrowed to a single country), nearest first when a distance
  // is available, since there's no one "right" country to pick for
  // them, for a Philippines visitor that's every retailer across
  // Singapore, Indonesia, and Malaysia combined, not just whichever of
  // those three happens to be nearest. The "By Country" pill names the
  // region instead of a country, and no dropdown option is
  // highlighted, since this view spans more than one of them.
  function showSubregionFallback(subregion, list, geo) {
    var sorted = list;
    if (geo && typeof geo.lat === 'number' && typeof geo.lng === 'number') {
      sorted = list.slice().sort(function (a, b) {
        var da = (typeof a.lat === 'number' && typeof a.lng === 'number') ? haversineMiles(geo.lat, geo.lng, a.lat, a.lng) : Infinity;
        var db = (typeof b.lat === 'number' && typeof b.lng === 'number') ? haversineMiles(geo.lat, geo.lng, b.lat, b.lng) : Infinity;
        return da - db;
      });
    }
    selectedCountryValue = '';
    var displayName = subregionDisplayName(subregion);
    if (countryCurrentLabelEl) countryCurrentLabelEl.textContent = displayName;
    countryMenuOptions().forEach(function (el) {
      el.classList.remove('is-selected');
      el.setAttribute('aria-selected', 'false');
    });
    renderResults(sorted, 'Retailers near you in ' + displayName);
    applyFilterToMap(sorted);
    scrollResultsIntoViewIfNeeded();
  }

  function applyInitialView(geo) {
    // The visitor already searched, picked a country, cleared the
    // filter, or clicked "Use My Location" while this was still in
    // flight; leave their own choice alone instead of clobbering it
    // with a stale default.
    if (userHasInteracted) return;

    if (geo) {
      var matchedCountry = matchRetailerCountry(geo.country);
      if (matchedCountry) {
        selectCountryOption(matchedCountry, matchedCountry);
        return;
      }

      // No retailer in the visitor's own country yet: show every
      // retailer in that same part of the world together (so a
      // Philippines visitor sees Singapore, Indonesia, and Malaysia
      // combined, all "South-eastern Asia," rather than being routed
      // to just one of them, or to Hong Kong, a different part of
      // Asia, just because it happens to be a few hundred miles closer).
      var visitorSubregion = countrySubregion(geo.country);
      if (visitorSubregion) {
        var subregionList = allRetailers.filter(function (r) {
          return countrySubregion(r.country) === visitorSubregion;
        });
        if (subregionList.length) {
          showSubregionFallback(visitorSubregion, subregionList, geo);
          return;
        }
      }

      // The visitor's own part of the world has no retailer at all (or
      // we don't recognize their country): fall through to the full,
      // unfiltered list below, same as a failed geo lookup. This used
      // to fall back further still, to whichever single retailer was
      // nearest anywhere on the globe, but once the retailer list has
      // no coverage at all in a visitor's part of the world, "nearest
      // of whatever's left, regardless of continent" stops meaning
      // "near" in any useful sense (a Philippines visitor being sent
      // to a single store in Australia, thousands of miles away, just
      // because it happened to be the closest of five countries total,
      // was more confusing than showing everything).
    }
    showAll();
  }

  function escapeHtml(str) {
    return (str || '').toString().replace(/[&<>"']/g, function (ch) {
      return (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
      );
    });
  }

  function scrollResultsIntoViewIfNeeded() {
    var isDesktop = false;
    try {
      isDesktop = window.matchMedia && window.matchMedia('(min-width: 861px)').matches;
    } catch (e) {}
    if (isDesktop) return;
    if (!resultsPanelEl) return;
    resultsPanelEl.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  function scrollMapIntoViewIfNeeded() {
    var isDesktop = false;
    try {
      isDesktop = window.matchMedia && window.matchMedia('(min-width: 861px)').matches;
    } catch (e) {}
    if (isDesktop) return;
    if (!mapWrapEl) return;
    mapWrapEl.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  var INFOWINDOW_CLOSE_ICON =
    '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

  var EXTERNAL_LINK_ICON =
    '<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.00028 0.5V7C9.00028 7.13261 8.9476 7.25979 8.85383 7.35355C8.76006 7.44732 8.63289 7.5 8.50028 7.5C8.36767 7.5 8.24049 7.44732 8.14672 7.35355C8.05296 7.25979 8.00028 7.13261 8.00028 7V1.70687L0.854028 8.85375C0.760208 8.94757 0.63296 9.00028 0.500278 9.00028C0.367596 9.00028 0.240348 8.94757 0.146528 8.85375C0.0527077 8.75993 0 8.63268 0 8.5C0 8.36732 0.0527077 8.24007 0.146528 8.14625L7.2934 1H2.00028C1.86767 1 1.74049 0.947321 1.64672 0.853553C1.55296 0.759785 1.50028 0.632608 1.50028 0.5C1.50028 0.367392 1.55296 0.240215 1.64672 0.146447C1.74049 0.0526785 1.86767 0 2.00028 0H8.50028C8.63289 0 8.76006 0.0526785 8.85383 0.146447C8.9476 0.240215 9.00028 0.367392 9.00028 0.5Z" fill="#A36043"/></svg>';

  function renderResults(list, headingText) {
    if (resultsHeadingEl) resultsHeadingEl.textContent = headingText;
    if (resultsCountEl) {
      var count = (list || []).length;
      resultsCountEl.textContent = count === 1 ? '1 store found' : count + ' stores found';
    }
    if (!resultsPanelEl) return;

    if (!list || !list.length) {
      resultsPanelEl.innerHTML = '<p class="pp-locator-empty">No retailers found. Try a different search or browse by country.</p>';
      return;
    }

    var html = '<ul class="pp-locator-list">';
    list.forEach(function (r) {
      var hasCoords = typeof r.lat === 'number' && typeof r.lng === 'number';
      var isMappable = hasGoogleMaps && hasCoords;

      // Three official tags, not a single Authorized Retailer/Distributor
      // badge: a store can carry any combination of these, so each is its
      // own pill. All three come straight from retailer data (JSON field
      // or the "Retailer" block checkboxes): a retailer can have a
      // website worth linking to (the separate "Visit Website" button,
      // below) without that site actually selling online, so "Online
      // Shop" needs its own online_store field rather than being
      // inferred from whether a website link exists.
      var tags = [];
      if (r.pet_care_service) tags.push({ cls: 'pp-locator-tag--pet-care', label: 'Pet Care Service' });
      if (r.online_store) tags.push({ cls: 'pp-locator-tag--online-shop', label: 'Online Shop' });
      if (r.in_store) tags.push({ cls: 'pp-locator-tag--in-store', label: 'In-Store' });

      // "is-active": the card that's currently selected/clicked, or
      // whose address is showing on the map right now (its info
      // window is open), see activePinKey / setActiveResultItem.
      var isActive = isMappable && activePinKey !== null && activePinKey === pinKey(r);
      html += '<li class="pp-locator-item' + (isMappable ? ' pp-locator-item--mappable' : '') + (isActive ? ' is-active' : '') + '"';
      if (isMappable) {
        html +=
          ' data-lat="' + r.lat + '" data-lng="' + r.lng + '"' +
          ' data-name="' + escapeHtml(r.name) + '"' +
          ' data-address="' + escapeHtml(r.address || '') + '"' +
          ' data-pin-key="' + escapeHtml(pinKey(r)) + '"';
      }
      html += '>';

      html += '<div class="pp-locator-item__top">';
      html += '<span class="pp-locator-item__name">' + escapeHtml(r.name) + '</span>';
      html += '</div>';

      var addressLine = [r.address, [r.city, r.state, r.zip].filter(Boolean).join(', '), r.country]
        .filter(Boolean)
        .join(', ');
      if (addressLine) {
        html += '<div class="pp-locator-item__address">' + addressLine + '</div>';
      }
      // Falls back to email when there's no phone on file, so a
      // retailer still has some way to reach them instead of this row
      // disappearing entirely. Phone takes priority when both exist,
      // same as before.
      if (r.phone) {
        html +=
          '<div class="pp-locator-item__phone"><span class="pp-locator-item__phone-label">Contact:</span> <a href="tel:' +
          escapeHtml(r.phone) + '">' + escapeHtml(r.phone) + '</a></div>';
      } else if (r.email) {
        html +=
          '<div class="pp-locator-item__phone"><span class="pp-locator-item__phone-label">Contact:</span> <a href="mailto:' +
          escapeHtml(r.email) + '">' + escapeHtml(r.email) + '</a></div>';
      }

      if (tags.length) {
        html += '<div class="pp-locator-item__tags">';
        tags.forEach(function (tag) {
          html += '<span class="pp-locator-tag ' + tag.cls + '">' + escapeHtml(tag.label) + '</span>';
        });
        html += '</div>';
      }

      html += '<div class="pp-locator-item__links">';
      if (r.website) {
        html +=
          '<a class="pp-locator-link-button pp-locator-link-button--outline" target="_blank" rel="noopener" href="' +
          escapeHtml(r.website) + '">Visit Website ' + EXTERNAL_LINK_ICON + '</a>';
      }
      // "Get Directions" is gated on having a street address, not just
      // coordinates: a retailer with lat/lng but no address (the online-
      // only sellers who asked not to show their street address, see
      // r.address below) still gets a pin on the map and still shows
      // city/state/zip, but intentionally has no map/directions link out
      // to a specific point, same as if the pin itself weren't precise
      // enough to send someone to.
      if (r.address && hasCoords) {
        html +=
          '<a class="pp-locator-link-button pp-locator-link-button--plain" target="_blank" rel="noopener" href="https://www.google.com/maps/dir/?api=1&destination=' +
          r.lat + ',' + r.lng + '">Get Directions</a>';
      } else if (r.address) {
        html +=
          '<a class="pp-locator-link-button pp-locator-link-button--plain" target="_blank" rel="noopener" href="https://www.google.com/maps/dir/?api=1&destination=' +
          encodeURIComponent([r.address, r.city, r.state, r.zip, r.country].filter(Boolean).join(', ')) +
          '">Get Directions</a>';
      }
      html += '</div>';

      html += '</li>';
    });
    html += '</ul>';

    resultsPanelEl.innerHTML = html;
  }

  // Marks one result card as active (or none, with a falsy key):
  // toggles the class on whatever's in the DOM right now, so it's
  // instant and doesn't need a full renderResults() re-render, and
  // also remembers the key so the next renderResults() call (search,
  // country change, "Use My Location", etc.) re-applies it to the
  // right card without needing to be told again.
  function setActiveResultItem(key) {
    activePinKey = key || null;
    if (!resultsPanelEl) return;
    var items = resultsPanelEl.querySelectorAll('.pp-locator-item--mappable');
    for (var i = 0; i < items.length; i++) {
      var isActive = !!activePinKey && items[i].getAttribute('data-pin-key') === activePinKey;
      items[i].classList.toggle('is-active', isActive);
    }
  }

  function applyFilterToMap(list, focusPoint) {
    // Remembered so the map resize handling below (see initMap) can
    // re-fit to whatever is currently showing, not whatever was
    // showing back when the map was first constructed.
    currentMapFilter = { list: list, focusPoint: focusPoint };
    if (!mapReady) {
      pendingFilter = { list: list, focusPoint: focusPoint };
      return;
    }
    styleMarkers(buildPinMatchSet(list));
    fitMapToList(list, focusPoint);
  }

  function styleMarkers(matchSet) {
    if (!markersByKey) return;
    Object.keys(markersByKey).forEach(function (key) {
      var entry = markersByKey[key];
      var isMatch = !!(matchSet && matchSet[key]);
      var isDimmed = !!(matchSet && !matchSet[key]);
      entry.marker.setIcon(pinIcon(isMatch ? PIN_SIZE_MATCH : PIN_SIZE));
      entry.marker.setOpacity(isDimmed ? PIN_DIMMED_OPACITY : 1);
      entry.marker.setZIndex(isMatch ? 500 : 1);
    });
  }

  function fitMapToList(list, focusPoint) {
    if (!map) return;
    if (focusPoint) {
      map.panTo(focusPoint);
      map.setZoom(Math.max(map.getZoom(), 9));
      return;
    }
    var coords = (list || []).filter(function (r) {
      return typeof r.lat === 'number' && typeof r.lng === 'number';
    });
    if (!coords.length) return;
    if (coords.length === 1) {
      map.panTo({ lat: coords[0].lat, lng: coords[0].lng });
      map.setZoom(14);
      return;
    }
    var bounds = new google.maps.LatLngBounds();
    coords.forEach(function (r) {
      bounds.extend({ lat: r.lat, lng: r.lng });
    });
    map.fitBounds(bounds, 40);
    google.maps.event.addListenerOnce(map, 'bounds_changed', function () {
      if (map.getZoom() > 15) map.setZoom(15);
    });
  }

  function openInfoWindow(marker, retailer) {
    if (!infoWindow) return;
    var addressLine = [retailer.city, [retailer.state, retailer.zip].filter(Boolean).join(' '), retailer.country]
      .filter(Boolean)
      .join(', ');
    var addressHtml = [
      retailer.address ? escapeHtml(retailer.address) : '',
      addressLine ? escapeHtml(addressLine) : ''
    ].filter(Boolean).join('<br>');

    // Our own close button and layout, not Google's default InfoWindow
    // chrome: the CSS (.gm-ui-hover-effect, .gm-style-iw-chr, etc.) hides
    // Google's native close button and the header row it reserves above
    // the content, so this custom one can sit flush at the top-left with
    // no dead space above the title. The click handler that makes this
    // button actually close the window is wired up once, in initMap(),
    // via the InfoWindow's "domready" event, since this HTML only becomes
    // real DOM (and only then has something to attach a listener to)
    // after Google inserts it on open.
    var content =
      '<div class="pp-locator-infowindow">' +
        '<button type="button" class="pp-locator-infowindow__close" aria-label="Close">' + INFOWINDOW_CLOSE_ICON + '</button>' +
        '<div class="pp-locator-infowindow__body">' +
          '<strong class="pp-locator-infowindow__name">' + escapeHtml(retailer.name) + '</strong>' +
          (addressHtml ? '<div class="pp-locator-infowindow__address">' + addressHtml + '</div>' : '') +
        '</div>' +
      '</div>';
    infoWindow.setContent(content);
    infoWindow.open(map, marker);
    // The retailer's address is now showing on the map: highlight its
    // card too, whether this was opened by clicking that card or by
    // clicking its pin directly on the map.
    setActiveResultItem(pinKey(retailer));
  }

  function focusMarker(retailer) {
    if (typeof retailer.lat !== 'number' || typeof retailer.lng !== 'number') return;
    if (!mapReady) {
      pendingFilter = { list: [retailer], focusPoint: { lat: retailer.lat, lng: retailer.lng } };
      return;
    }
    map.panTo({ lat: retailer.lat, lng: retailer.lng });
    map.setZoom(Math.max(map.getZoom(), 14));
    var entry = markersByKey[pinKey(retailer)];
    // Use the full retailer record already stored on the marker entry
    // (see the "markersByKey[pinKey(r)] = { marker: marker, retailer: r }"
    // assignment in initMap), not the partial { lat, lng, name, address }
    // object this function is sometimes called with (see the results-list
    // click handler below): that one has no city/state/zip/country, so the
    // info window would show only a street line, or nothing at all for a
    // retailer with no address, even though the full address is right
    // there on the marker already.
    if (entry) openInfoWindow(entry.marker, entry.retailer);
    scrollMapIntoViewIfNeeded();
    if (statusEl) statusEl.textContent = 'Showing ' + retailer.name + ' on the map.';
  }

  function showCountry(countryName) {
    var key = norm(countryName);
    var list = byCountryNorm[key] || [];
    renderResults(list, countryName);
    applyFilterToMap(list);
    scrollResultsIntoViewIfNeeded();
  }

  function showAll() {
    renderResults(allRetailers, 'All authorized retailers');
    applyFilterToMap(allRetailers);
  }

  function runSearch(query) {
    var q = norm(query);
    if (!q) {
      showAll();
      return;
    }
    var matches = allRetailers.filter(function (r) {
      return (
        norm(r.name).indexOf(q) !== -1 ||
        norm(r.city).indexOf(q) !== -1 ||
        norm(r.state).indexOf(q) !== -1 ||
        norm(r.zip).indexOf(q) !== -1 ||
        norm(r.country).indexOf(q) !== -1 ||
        norm(r.address).indexOf(q) !== -1
      );
    });
    renderResults(matches, 'Search results for "' + query + '"');
    applyFilterToMap(matches);
    scrollResultsIntoViewIfNeeded();
  }

  function countryOptionHtml(value, label, isSelected) {
    return (
      '<li class="pp-locator__country-option' + (isSelected ? ' is-selected' : '') + '"' +
      ' role="option" aria-selected="' + (isSelected ? 'true' : 'false') + '"' +
      ' data-value="' + escapeHtml(value) + '" tabindex="-1">' +
      LOCATION_PIN_ICON +
      '<span>' + escapeHtml(label) + '</span>' +
      '</li>'
    );
  }

  function populateCountrySelect() {
    if (!countryMenuEl) return;
    var names = {};
    allRetailers.forEach(function (r) {
      if (r.country) names[norm(r.country)] = r.country;
    });
    var sorted = Object.keys(names)
      .map(function (key) { return names[key]; })
      .sort(function (a, b) { return a.localeCompare(b); });

    var html = countryOptionHtml('', 'All countries', selectedCountryValue === '');
    sorted.forEach(function (name) {
      html += countryOptionHtml(name, name, norm(selectedCountryValue) === norm(name));
    });
    countryMenuEl.innerHTML = html;
  }

  function countryMenuOptions() {
    return countryMenuEl ? Array.prototype.slice.call(countryMenuEl.querySelectorAll('.pp-locator__country-option')) : [];
  }

  function openCountryMenu() {
    if (!countryMenuEl || !countryTriggerEl) return;
    countryMenuEl.hidden = false;
    countryTriggerEl.setAttribute('aria-expanded', 'true');
    var options = countryMenuOptions();
    var active = options.filter(function (el) { return el.classList.contains('is-selected'); })[0] || options[0];
    options.forEach(function (el) { el.tabIndex = el === active ? 0 : -1; });
    if (active) active.focus();

    document.addEventListener('click', onDocumentClickCloseMenu, true);
  }

  function closeCountryMenu(returnFocus) {
    if (!countryMenuEl || !countryTriggerEl) return;
    if (countryMenuEl.hidden) return;
    countryMenuEl.hidden = true;
    countryTriggerEl.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', onDocumentClickCloseMenu, true);
    if (returnFocus) countryTriggerEl.focus();
  }

  function onDocumentClickCloseMenu(event) {
    if (countryTriggerEl && countryTriggerEl.contains(event.target)) return;
    if (countryMenuEl && countryMenuEl.contains(event.target)) return;
    closeCountryMenu(false);
  }

  function selectCountryOption(value, label) {
    selectedCountryValue = value;
    if (countryCurrentLabelEl) countryCurrentLabelEl.textContent = label;
    countryMenuOptions().forEach(function (el) {
      var isSelected = el.getAttribute('data-value') === value;
      el.classList.toggle('is-selected', isSelected);
      el.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
    closeCountryMenu(true);
    if (value) {
      showCountry(value);
    } else {
      showAll();
    }
  }

  function setUpCountryMenu() {
    if (!countryTriggerEl || !countryMenuEl) return;

    countryTriggerEl.addEventListener('click', function () {
      if (countryMenuEl.hidden) {
        openCountryMenu();
      } else {
        closeCountryMenu(false);
      }
    });

    countryMenuEl.addEventListener('click', function (event) {
      var option = event.target.closest('.pp-locator__country-option');
      if (!option) return;
      markUserInteracted();
      selectCountryOption(option.getAttribute('data-value') || '', option.textContent.trim());
    });

    countryMenuEl.addEventListener('keydown', function (event) {
      var options = countryMenuOptions();
      if (!options.length) return;
      var currentIndex = options.indexOf(document.activeElement);
      if (currentIndex === -1) currentIndex = 0;

      function focusOption(index) {
        options.forEach(function (el, i) {
          el.tabIndex = i === index ? 0 : -1;
        });
        options[index].focus();
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        focusOption(Math.min(currentIndex + 1, options.length - 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        focusOption(Math.max(currentIndex - 1, 0));
      } else if (event.key === 'Home') {
        event.preventDefault();
        focusOption(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        focusOption(options.length - 1);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        var active = options[currentIndex];
        markUserInteracted();
        selectCountryOption(active.getAttribute('data-value') || '', active.textContent.trim());
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeCountryMenu(true);
      } else if (event.key === 'Tab') {
        closeCountryMenu(false);
      }
    });

    countryTriggerEl.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openCountryMenu();
      }
    });
  }

  function setUpGeolocation() {
    if (!geoBtnEl) return;
    if (!('geolocation' in navigator)) return;
    geoBtnEl.hidden = false;

    geoBtnEl.addEventListener('click', function () {
      markUserInteracted();
      if (statusEl) statusEl.textContent = 'Locating you...';
      geoBtnEl.disabled = true;

      navigator.geolocation.getCurrentPosition(
        function (pos) {
          geoBtnEl.disabled = false;
          var userLat = pos.coords.latitude;
          var userLng = pos.coords.longitude;

          var withDistance = allRetailers
            .filter(function (r) { return typeof r.lat === 'number' && typeof r.lng === 'number'; })
            .map(function (r) {
              return Object.assign({}, r, { _distanceMiles: haversineMiles(userLat, userLng, r.lat, r.lng) });
            })
            .sort(function (a, b) { return a._distanceMiles - b._distanceMiles; })
            .slice(0, 15);

          if (!withDistance.length) {
            if (statusEl) statusEl.textContent = 'No geocoded retailers nearby yet.';
            return;
          }

          renderResults(withDistance, 'Nearest to you');
          applyFilterToMap(withDistance, { lat: userLat, lng: userLng });
          scrollResultsIntoViewIfNeeded();
          if (statusEl) statusEl.textContent = 'Showing the ' + withDistance.length + ' nearest retailers to you.';
        },
        function () {
          geoBtnEl.disabled = false;
          if (statusEl) statusEl.textContent = 'Could not get your location. Please allow location access and try again.';
        }
      );
    });
  }

  // Retailers added as "Retailer" blocks in the theme editor (see the
  // section's schema), separate from the bulk list in retailers.json so
  // admin can add new stores without touching JSON or code. The section's
  // Liquid markup serializes section.blocks into this script tag; read it
  // defensively since block settings can be blank or hand-edited.
  function readBlockRetailers() {
    var el = document.getElementById('pp-locator-block-data');
    if (!el) return [];
    var parsed;
    try {
      parsed = JSON.parse(el.textContent || '[]');
    } catch (e) {
      return [];
    }
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(function (r) { return r && r.name; })
      .map(function (r) {
        var out = Object.assign({}, r);
        var lat = parseFloat(r.lat);
        var lng = parseFloat(r.lng);
        if (!isNaN(lat) && !isNaN(lng)) {
          out.lat = lat;
          out.lng = lng;
        } else {
          delete out.lat;
          delete out.lng;
        }
        return out;
      });
  }

  function showMapKeyMissing() {
    if (!mapEl) return;
    mapEl.innerHTML =
      '<p class="pp-locator-empty pp-locator__map-placeholder">Add a Google Maps API key in the theme editor to show the map here. Search and the list below still work fully without it.</p>';
  }

  function showMapLoadError() {
    if (!mapEl) return;
    mapEl.innerHTML =
      '<p class="pp-locator-empty pp-locator__map-placeholder">The map could not load. Double check the Google Maps API key in the theme editor, and that billing is enabled for it in Google Cloud Console.</p>';
  }

  function loadGoogleMaps() {
    return new Promise(function (resolve, reject) {
      if (window.google && window.google.maps) {
        resolve(window.google.maps);
        return;
      }
      var callbackName = 'ppLocatorGoogleMapsReady';
      window[callbackName] = function () {
        delete window[callbackName];
        resolve(window.google.maps);
      };
      var script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(googleMapsKey) +
        '&callback=' + callbackName;
      script.async = true;
      script.onerror = function () {
        delete window[callbackName];
        reject(new Error('Google Maps failed to load'));
      };
      document.head.appendChild(script);
    });
  }

  function initMap(maps) {
    map = new maps.Map(mapEl, {
      center: { lat: 20, lng: 0 },
      zoom: 2,
      minZoom: 2,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      clickableIcons: false,
      gestureHandling: 'cooperative',
      styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }]
    });
    infoWindow = new maps.InfoWindow();
    infoWindow.addListener('domready', function () {
      var closeBtn = document.querySelector('.pp-locator-infowindow__close');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          infoWindow.close();
          setActiveResultItem(null);
        });
      }
    });

    markersByKey = {};
    var clusterMarkers = [];
    allRetailers.forEach(function (r) {
      if (typeof r.lat !== 'number' || typeof r.lng !== 'number') return;
      var marker = new maps.Marker({
        position: { lat: r.lat, lng: r.lng },
        icon: pinIcon(PIN_SIZE),
        title: r.name
      });
      marker.addListener('click', function () {
        openInfoWindow(marker, r);
      });
      markersByKey[pinKey(r)] = { marker: marker, retailer: r };
      clusterMarkers.push(marker);
    });

    var clusterRenderer = {
      render: function (cluster) {
        var count = cluster.count;
        var size = count < 10 ? 36 : count < 50 ? 44 : 52;
        return new maps.Marker({
          position: cluster.position,
          icon: {
            path: maps.SymbolPath.CIRCLE,
            scale: size / 2,
            fillColor: CLUSTER_COLOR,
            fillOpacity: 0.92,
            strokeColor: '#FFFFFF',
            strokeWeight: 2
          },
          label: { text: String(count), color: '#FFFFFF', fontSize: '13px', fontWeight: '700' },
          zIndex: 1000 + count
        });
      }
    };

    if (window.markerClusterer && window.markerClusterer.MarkerClusterer) {
      clusterer = new window.markerClusterer.MarkerClusterer({
        map: map,
        markers: clusterMarkers,
        renderer: clusterRenderer
      });
    } else {
      clusterMarkers.forEach(function (marker) { marker.setMap(map); });
    }

    mapReady = true;
    var pending = pendingFilter || { list: allRetailers, focusPoint: null };
    pendingFilter = null;

    // Apply the pending filter right away. An earlier version of this
    // code waited for the map's "idle" event first, on the theory that
    // fitBounds needs the map to have finished its first render to
    // compute a correct zoom. In practice, on the live site, "idle"
    // doesn't reliably fire at all at this point, which meant this
    // initial fit silently never ran, for every filter, not just one
    // case: the map stayed at its default world view no matter what
    // was selected. Calling it immediately, same as every later filter
    // change (search, country pick, "Use My Location") already does,
    // is simpler and doesn't depend on an event that may not come. The
    // ResizeObserver below is what now handles the case this "idle"
    // wait was originally meant for: a container whose real size isn't
    // settled yet at construction time.
    applyFilterToMap(pending.list, pending.focusPoint);

    // Google Maps measures the map container's pixel size once, when
    // it's first constructed, and never re-measures it on its own. If
    // that size is wrong at construction time, or changes afterward
    // (the sticky two-column layout settling, fonts or images loading
    // and shifting the page, the section becoming visible after being
    // lazy-rendered, a window resize), every later fitBounds/panTo
    // keeps computing against the stale size, which is exactly the
    // kind of "list is filtered correctly but the map stays zoomed
    // out" mismatch this section has seen. Watching the actual
    // container element with ResizeObserver, not just window resize,
    // catches all of those cases: whenever its size changes, tell
    // Google Maps to re-measure ("resize") and re-fit to whatever
    // filter is currently showing.
    if (window.ResizeObserver && mapEl) {
      var resizeRefitTimer = null;
      var mapResizeObserver = new window.ResizeObserver(function () {
        if (!mapReady) return;
        clearTimeout(resizeRefitTimer);
        resizeRefitTimer = setTimeout(function () {
          maps.event.trigger(map, 'resize');
          fitMapToList(currentMapFilter.list, currentMapFilter.focusPoint);
        }, 150);
      });
      mapResizeObserver.observe(mapEl);
    }
  }

  var dataPromise = fetch(retailersUrl)
    .then(function (res) {
      if (!res.ok) throw new Error('bad response');
      return res.json();
    })
    .then(function (data) {
      var jsonRetailers = (data && data.retailers) || [];
      var blockRetailers = readBlockRetailers();
      allRetailers = jsonRetailers.concat(blockRetailers);
      byCountryNorm = groupByCountry(allRetailers);
      if (data && data.sampleData && sampleBannerEl) sampleBannerEl.hidden = false;
      populateCountrySelect();
      setUpGeolocation();
      return geoIpPromise.then(applyInitialView);
    })
    .catch(function () {
      if (resultsPanelEl) {
        resultsPanelEl.innerHTML =
          '<p class="pp-locator-empty">The store locator could not load right now. Please refresh the page, or contact us and we will help you find a retailer near you.</p>';
      }
    });

  if (hasGoogleMaps) {
    loadGoogleMaps()
      .then(function (maps) {
        return dataPromise.then(function () {
          initMap(maps);
        });
      })
      .catch(function () {
        showMapLoadError();
      });
  } else {
    showMapKeyMissing();
  }

  if (searchInputEl) {
    searchInputEl.addEventListener('input', function () {
      markUserInteracted();
      runSearch(searchInputEl.value);
    });
  }

  setUpCountryMenu();

  if (clearBtnEl) {
    clearBtnEl.addEventListener('click', function () {
      markUserInteracted();
      if (searchInputEl) searchInputEl.value = '';
      selectedCountryValue = '';
      if (countryCurrentLabelEl) countryCurrentLabelEl.textContent = 'All countries';
      countryMenuOptions().forEach(function (el) {
        var isSelected = el.getAttribute('data-value') === '';
        el.classList.toggle('is-selected', isSelected);
        el.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });
      showAll();
      scrollResultsIntoViewIfNeeded();
    });
  }

  if (resultsPanelEl) {
    resultsPanelEl.addEventListener('click', function (event) {
      var target = event.target.closest('.pp-locator-item--mappable');
      if (!target) return;

      var lat = parseFloat(target.getAttribute('data-lat'));
      var lng = parseFloat(target.getAttribute('data-lng'));
      if (isNaN(lat) || isNaN(lng)) return;

      focusMarker({
        lat: lat,
        lng: lng,
        name: target.getAttribute('data-name') || '',
        address: target.getAttribute('data-address') || ''
      });
    });
  }
})();